import { Card } from './card';

/**
 * 잡 종류. UseCardData.ts/StockPageContext.tsx가 각자 `job.type === 'X'` if 체인으로
 * 완료 처리를 분기하던 걸 `Record<JobType, handler>` 맵으로 통일하면서(2026-08-11)
 * 이 유니온을 뽑아냈다 — 예전엔 `type: string`이라 오탈자가 있어도 컴파일이 통과했다.
 */
export type JobType =
  | 'UPDATE_PRICES'
  | 'FETCH_STATUS'
  | 'SYNC_NAVER'
  | 'SYNC_STOCK'
  | 'APPLY_STOCK'
  | 'IMPORT_PRODUCTS'
  | 'REGISTER_PRODUCTS'
  | 'CLASSIFY_DROPS'
  /** 판매가 결정 묶음(감사 card-controltower#340 · #341) — 결과는 decisions. */
  | 'DECIDED_PRICE'
  /**
   * 프런트 전용 — 서버가 한 번도 상태를 주지 않고 사라진 잡(첫 조회부터 404)이라 종류를 모를 때(감사 card-controltower#87).
   * 백엔드 JobType 에는 없다. 예전엔 이때 'REGISTER_PRODUCTS' 로 채워 가격 · 재고 작업도 "상품 등록 실패" 로 안내했다.
   */
  | 'UNKNOWN';

export interface JobStatus {
  jobId:          string;
  type:           JobType;
  status:         'RUNNING' | 'DONE' | 'FAILED';
  phase:          string;
  processedCount: number;
  total:          number;
  results:        Card[] | null;
  successCount:   number;
  failCount:      number;
  /**
   * naver.commerce.register-enabled=false(dry-run)로 처리된 건수(2026-08-19 추가) —
   * REGISTER_PRODUCTS 잡에서만 0보다 클 수 있다. dry-run도 success=true로 카운트되기
   * 때문에, 이 값이 successCount와 같으면 "성공"이 아니라 전부 dry-run이었다는 뜻
   * (NAVER_REGISTER_ENABLED 환경변수 누락으로 진행률 모달이 "성공"으로만 보이던 사고
   * 이후 추가 — 다른 잡 타입은 항상 0).
   */
  dryRunCount:    number;
  /**
   * 스토어에서 완전히 삭제돼 로컬에서도 소프트 삭제된 건수(2026-08-19 추가) —
   * IMPORT_PRODUCTS/SYNC_STOCK에서만 0보다 클 수 있다. successCount(신규 등록)가
   * 0이어도 이 값이 0보다 크면 실제로는 정리 작업이 있었다는 뜻이라, 완료 토스트가
   * successCount만 보고 "신규 등록 0건"으로만 표시하면 아무 일도 안 일어난 것처럼
   * 보이는 문제가 있었다("네이버에서 상품 가져오기" 버튼이 반응 없는 것처럼 오인된
   * 실제 사례 — 판매자가 스토어에서 삭제한 상품이 로컬에도 삭제되긴 했지만 신규
   * 등록분은 없었던 경우).
   */
  deletedCount:   number;
  /**
   * ① 가격 받기(UPDATE_PRICES)에서 PriceHub 가 이 매장을 알아보지 못해 공통가를 대신 받은 카드 수(감사 card-controltower#181,
   * 백엔드 JobStatus.storeUnresolvedCount). 그 값도 <매장> 가격 자리에 쓰지만 출처를 화면에 분명히 보인다. 예전 서버면 없음.
   */
  storeUnresolvedCount?: number;
  /**
   * 판매가 결정 묶음(DECIDED_PRICE)의 항목별 결과(요청 순서, 단건 PUT 응답과 같은 모양) — 다른 잡 · 옛 서버는 없음 · null.
   * 모양 검사는 utils/decidedPrice 의 normalizeBulkResults 가 한다.
   */
  decisions?:     unknown[] | null;
  /**
   * ③ 스토어 반영(SYNC_NAVER)에서 확정 대기 · 요청 대기로 건너뛴 카드 수(백엔드 PR #76 — 실패가 아니라 건너뜀, failCount 에서 뺌).
   * 옛 서버는 없음.
   */
  skippedCount?:  number | null;
  error:          string | null;
}

/** 잡 종류(JobStatus.type) → 화면 표시 라벨. JobProgressBar/JobModal이 공유한다. */
export const JOB_TYPE_LABEL: Record<JobType, string> = {
  // ① 단계 — 네이버 동기화 + PriceHub <매장> 가격 받기 + 시세 분류(세 화면 재설계 C10, 감사 #489). 옛 이름 '수정 가격 조회'.
  UPDATE_PRICES: '가격 받기',
  FETCH_STATUS:  '판매 상태 조회',
  SYNC_NAVER:    'Naver 반영',
  // 재고뿐 아니라 상품명·이미지·네이버 가격·판매상태까지 Naver 값으로 최신화하는 잡이라
  // "재고 동기화"라는 이름이 좁아 보였다 — 진행바/완료 모달에 "네이버스토어 상품
  // 동기화"로 노출(2026-07-29, 사용자 요청).
  SYNC_STOCK:    '네이버스토어 상품 동기화',
  APPLY_STOCK:   '재고 반영',
  IMPORT_PRODUCTS: '상품 가져오기',
  REGISTER_PRODUCTS: '상품 등록',
  CLASSIFY_DROPS: '타 샵 시세 대조 분류',
  DECIDED_PRICE: '판매가 결정',
  UNKNOWN: '작업',
};